import { UserRole } from '../../features/user-management';

/**
 * User role display names and utilities
 */

// Friendly display names for user roles
export const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  [UserRole.SYSTEM_ADMIN]: 'System Administrator',
  [UserRole.UNIVERSITY_ADMIN]: 'University Administrator',
  [UserRole.FACULTY_ADMIN]: 'Institute Administrator',
  [UserRole.PROFESSOR]: 'Lecturer',
  [UserRole.COURSE_REPRESENTATIVE]: 'Course Representative',
};

// Short display names for compact UI elements
export const ROLE_SHORT_NAMES: Record<UserRole, string> = {
  [UserRole.SYSTEM_ADMIN]: 'SYS ADMIN',
  [UserRole.UNIVERSITY_ADMIN]: 'Administrator',
  [UserRole.FACULTY_ADMIN]: 'Administrator',
  [UserRole.PROFESSOR]: 'Lecturer',
  [UserRole.COURSE_REPRESENTATIVE]: 'Course Rep',
};

// Role descriptions for tooltips and help text
export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  [UserRole.SYSTEM_ADMIN]: 'Full system access with ability to manage all universities and users',
  [UserRole.UNIVERSITY_ADMIN]: 'Manages university settings, institutes, and university-wide users',
  [UserRole.FACULTY_ADMIN]:
    'Manages institute subjects, lecturers, and institute-specific settings',
  [UserRole.PROFESSOR]: 'Lectures on subjects and manages course content and evaluations',
  [UserRole.COURSE_REPRESENTATIVE]: 'Student representative for a specific course or subject',
};

// Role badge colors for UI consistency
export const ROLE_COLORS: Record<UserRole, string> = {
  [UserRole.SYSTEM_ADMIN]: '#D32F2F', // Red
  [UserRole.UNIVERSITY_ADMIN]: '#7B1FA2', // Purple
  [UserRole.FACULTY_ADMIN]: '#1976D2', // Blue
  [UserRole.PROFESSOR]: '#388E3C', // Green
  [UserRole.COURSE_REPRESENTATIVE]: '#F57C00', // Orange
};

// Role badge variants for MUI Chip component
export const ROLE_CHIP_VARIANTS: Record<
  UserRole,
  'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
> = {
  [UserRole.SYSTEM_ADMIN]: 'error',
  [UserRole.UNIVERSITY_ADMIN]: 'secondary',
  [UserRole.FACULTY_ADMIN]: 'primary',
  [UserRole.PROFESSOR]: 'success',
  [UserRole.COURSE_REPRESENTATIVE]: 'warning',
};

// Role hierarchy levels (higher number = more permissions)
export const ROLE_HIERARCHY: Record<UserRole, number> = {
  [UserRole.SYSTEM_ADMIN]: 100,
  [UserRole.UNIVERSITY_ADMIN]: 80,
  [UserRole.FACULTY_ADMIN]: 60,
  [UserRole.PROFESSOR]: 40,
  [UserRole.COURSE_REPRESENTATIVE]: 20,
};

/**
 * Get the display name for a user role
 * @param role The user role enum value
 * @param short Whether to return the short version
 * @returns The display name string
 */
export const getRoleDisplayName = (role: UserRole | string, short = false): string => {
  const roleEnum = role as UserRole;
  if (short) {
    return ROLE_SHORT_NAMES[roleEnum] || roleEnum;
  }
  return ROLE_DISPLAY_NAMES[roleEnum] || roleEnum;
};

/**
 * Get the description for a user role
 * @param role The user role enum value
 * @returns The role description
 */
export const getRoleDescription = (role: UserRole | string): string => {
  const roleEnum = role as UserRole;
  return ROLE_DESCRIPTIONS[roleEnum] || 'No description available';
};

/**
 * Get the color for a user role
 * @param role The user role enum value
 * @returns The hex color code
 */
export const getRoleColor = (role: UserRole | string): string => {
  const roleEnum = role as UserRole;
  return ROLE_COLORS[roleEnum] || '#757575';
};

/**
 * Get the MUI Chip variant for a user role
 * @param role The user role enum value
 * @returns The chip variant
 */
export const getRoleChipVariant = (
  role: UserRole | string
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  const roleEnum = role as UserRole;
  return ROLE_CHIP_VARIANTS[roleEnum] || 'default';
};

/**
 * Check if a role has higher privileges than another
 * @param role1 The first role to compare
 * @param role2 The second role to compare
 * @returns True if role1 has higher privileges than role2
 */
export const isRoleHigherThan = (role1: UserRole | string, role2: UserRole | string): boolean => {
  const role1Enum = role1 as UserRole;
  const role2Enum = role2 as UserRole;
  const level1 = ROLE_HIERARCHY[role1Enum] || 0;
  const level2 = ROLE_HIERARCHY[role2Enum] || 0;
  return level1 > level2;
};

/**
 * Get all roles that are lower in hierarchy than the given role
 * @param role The role to compare against
 * @returns Array of roles with lower privileges
 */
export const getLowerRoles = (role: UserRole | string): UserRole[] => {
  const roleEnum = role as UserRole;
  const currentLevel = ROLE_HIERARCHY[roleEnum] || 0;

  return Object.entries(ROLE_HIERARCHY)
    .filter(([_, level]) => level < currentLevel)
    .map(([roleKey]) => roleKey as UserRole);
};

/**
 * Get all roles that are higher in hierarchy than the given role
 * @param role The role to compare against
 * @returns Array of roles with higher privileges
 */
export const getHigherRoles = (role: UserRole | string): UserRole[] => {
  const roleEnum = role as UserRole;
  const currentLevel = ROLE_HIERARCHY[roleEnum] || 0;

  return Object.entries(ROLE_HIERARCHY)
    .filter(([_, level]) => level > currentLevel)
    .map(([roleKey]) => roleKey as UserRole);
};

/**
 * Format multiple roles for display
 * @param roles Array of user roles
 * @param short Whether to use short names
 * @returns Formatted string of role names
 */
export const formatMultipleRoles = (roles: (UserRole | string)[], short = false): string => {
  if (!roles || roles.length === 0) return 'No roles';
  if (roles.length === 1) return getRoleDisplayName(roles[0], short);

  const displayNames = roles.map((role) => getRoleDisplayName(role, short));
  if (roles.length === 2) {
    return displayNames.join(' and ');
  }

  return `${displayNames.slice(0, -1).join(', ')}, and ${displayNames[displayNames.length - 1]}`;
};

/**
 * Get the appropriate icon name for a user role (for use with icon libraries)
 * @param role The user role
 * @returns Icon name string
 */
export const getRoleIconName = (role: UserRole | string): string => {
  const iconMap: Record<UserRole, string> = {
    [UserRole.SYSTEM_ADMIN]: 'AdminPanelSettings',
    [UserRole.UNIVERSITY_ADMIN]: 'School',
    [UserRole.FACULTY_ADMIN]: 'Business',
    [UserRole.PROFESSOR]: 'School',
    [UserRole.COURSE_REPRESENTATIVE]: 'Groups',
  };

  const roleEnum = role as UserRole;
  return iconMap[roleEnum] || 'Person';
};
